import React from "react";
import { useTranslation } from "react-i18next";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import { useWatch } from "react-hook-form";
import dayjs from "dayjs";

const PAGE_LOCALE_KEY = "page.merchants.";
const FORM_LABEL_KEY = PAGE_LOCALE_KEY + "merchantDetail.config.";

const ConfigSection = ({
  formProps,
  isEdit,
  isLoading,
  showSettlementMode = false,
}) => {
  const { t } = useTranslation();

  const isDisabled = !isEdit || isLoading;
  const settleMode = useWatch({ control: formProps.control, name: "settleMode" }) || "FIXED_DELAY";
  const settleModeLabel =
    settleMode === "BATCH" ? "Batch Settlement" : "Fixed Delay";

  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const fieldsConfig = {
    //Report
    autoTransactionReceipt: {
      fieldType: "checkbox",
      fieldName: "autoTransactionReceipt",
      label: t(`${FORM_LABEL_KEY}report.fields.autoTransactionReceipt`),
      disabled: isDisabled,
    },
    autoSettlementReport: {
      fieldType: "checkbox",
      fieldName: "autoSettlementReport",
      label: t(`${FORM_LABEL_KEY}report.fields.autoSettlementReport`),
      disabled: isDisabled,
    },
    autoDailyReport: {
      fieldType: "checkbox",
      fieldName: "autoDailyReport",
      label: t(`${FORM_LABEL_KEY}report.fields.autoDailyReport`),
      disabled: isDisabled,
    },

    summaryReportTime: {
      fieldType: "timePicker",
      fieldName: "summaryReportTime",
      label: t(`${FORM_LABEL_KEY}report.fields.summaryReportTime`),
      disabled: isDisabled,
      config: {
        rules: ["validDate"],
        defaultValue: dayjs("03:00:00", "HH:mm:ss")
      },
    },

    //Finance and Settlement
    settleMode: {
      fieldType: "textDisplay",
      fieldName: "settleMode",
      label: t(`${FORM_LABEL_KEY}financeAndSettlement.fields.settleMode`),
      config: {
        displayValue: settleModeLabel,
      },
    },

    settleCycle: {
      fieldType: "textInput",
      fieldName: "settleCycle",
      label: t(`${FORM_LABEL_KEY}financeAndSettlement.fields.settleCycle`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger"],
        sanitizers: ["integer", "range:[0,]"],
        defaultValue: 2,
      }
    },

    autoApproved: {
      fieldType: "checkbox",
      fieldName: "autoApproved",
      label: t(`${FORM_LABEL_KEY}financeAndSettlement.fields.autoApproved`),
      disabled: isDisabled,
      config: {
        defaultValue: true,
      },
    },

    batchAnchorDate: {
      fieldType: "datePicker",
      fieldName: "batchAnchorDate",
      label: t(`${FORM_LABEL_KEY}financeAndSettlement.fields.batchAnchorDate`),
      disabled: true,
    },
    batchUnit: {
      fieldType: "selection",
      fieldName: "batchUnit",
      label: t(`${FORM_LABEL_KEY}financeAndSettlement.fields.batchUnit`),
      disabled: true,
      config: {
        options: [
          { key: "DAY", title: "Day", value: "DAY" },
          { key: "WEEK", title: "Week", value: "WEEK" },
          { key: "MONTH", title: "Month", value: "MONTH" },
        ],
      },
    },
    batchInterval: {
      fieldType: "textInput",
      fieldName: "batchInterval",
      label: t(`${FORM_LABEL_KEY}financeAndSettlement.fields.batchInterval`),
      disabled: true,
    },
    batchDelayDay: {
      fieldType: "textInput",
      fieldName: "batchDelayDay",
      label: t(`${FORM_LABEL_KEY}financeAndSettlement.fields.batchDelayDay`),
      disabled: true,
    },

    smallTicketCharge: {
      fieldType: "checkbox",
      fieldName: "smallTicketCharge",
      label: t(`${FORM_LABEL_KEY}financeAndSettlement.fields.smallTicketCharge`),
      disabled: isDisabled,
      config: {
        defaultValue: true
      }
    },
    waiveThermalPaperFees: {
      fieldType: "checkbox",
      fieldName: "waiveThermalPaperFees",
      label: t(`${FORM_LABEL_KEY}financeAndSettlement.fields.waiveThermalPaperFees`),
      disabled: isDisabled,
    },
    zeroPlan: {
      fieldType: "checkbox",
      fieldName: "zeroPlan",
      label: t(`${FORM_LABEL_KEY}financeAndSettlement.fields.zeroPlan`),
      disabled: isDisabled,
    },

    merchantRemarks: {
      fieldType: "textInput",
      fieldName: "merchantRemarks",
      label: t(`${FORM_LABEL_KEY}financeAndSettlement.fields.merchantRemarks`),
      disabled: isDisabled,
      config: {
        maxLength: 255,
        disableLineBreak: true,
        multiline: true,
        rows: 3,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "noLineBreaks"
        ],
      },
    },

    //Receipt
    merchantLogo: {
      fieldType: "imagePicker",
      fieldName: "merchantLogo",
      label: "Merchant Logo",
      disabled: isDisabled,
      config: {
        labelRemarks: `(Maximum image size: 1MB; Format: jpg and png only)`,
        allowedMimeTypes: ["image/jpeg", "image/png"],
        maxSize: 1 * 1024 * 1024,
      }
    },

    //Campaign
    isKDollar: {
      fieldType: "checkbox",
      fieldName: "isKDollar",
      label: t(`${FORM_LABEL_KEY}campaign.fields.isKDollar`),
      disabled: isDisabled,
    },
    enableCampaign: {
      fieldType: "checkbox",
      fieldName: "enableCampaign",
      label: t(`${FORM_LABEL_KEY}campaign.fields.enableCampaign`),
      disabled: isDisabled,
    },

    //Others
    cooldownTime: {
      fieldType: "textInput",
      fieldName: "cooldownTime",
      label: t(`${FORM_LABEL_KEY}others.fields.cooldownTime`),
      disabled: isDisabled,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyInteger"],
        sanitizers: ["integer", "range:[0,]"],
        defaultValue: "5000",
      },
    },
    monthlyTransLimit: {
      fieldType: "textInput",
      fieldName: "monthlyTransLimit",
      label: t(`${FORM_LABEL_KEY}others.fields.monthlyTransLimit`),
      disabled: isDisabled,
      config: {
        inputType: "number",
        asNumber: true,
        rules: ["onlyPositiveNumber"],
        sanitizers: ["decimal:2", "range:[0,]"],
        defaultValue: 0,
      }
    }
  }

  return (
    <FormBuilder.Section>
      <FormBuilder.Section
        title={t(`${FORM_LABEL_KEY}report.title`)}
        titleVariant="h3">
        <FormBuilder.Row itemsPerRow={2}>
          {FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.autoTransactionReceipt,
              fieldsConfig.autoSettlementReport,
              fieldsConfig.autoDailyReport
            ]
          })}
        </FormBuilder.Row>
        <FormBuilder.Row itemsPerRow={1}>
          {FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.summaryReportTime,
            ]
          })}
        </FormBuilder.Row>
      </FormBuilder.Section>

      <FormBuilder.Section
        title={t(`${FORM_LABEL_KEY}financeAndSettlement.title`)}
        titleVariant="h3">
        {(!showSettlementMode || settleMode === "FIXED_DELAY") && (
          <FormBuilder.Row itemsPerRow={1}>
            {FormBuilder.buildFields({
              formProps,
              fields: [
                fieldsConfig.settleCycle,
              ]
            })}
          </FormBuilder.Row>
        )}
        {showSettlementMode && settleMode === "BATCH" && (
          <FormBuilder.Section title="Batch Settlement" titleVariant="h4">
            <FormBuilder.Row itemsPerRow={4}>
              {FormBuilder.buildFields({
                formProps,
                fields: [
                  fieldsConfig.batchAnchorDate,
                  fieldsConfig.batchUnit,
                  fieldsConfig.batchInterval,
                  fieldsConfig.batchDelayDay,
                ]
              })}
            </FormBuilder.Row>
          </FormBuilder.Section>
        )}
        <FormBuilder.Row itemsPerRow={1}>
          {FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.autoApproved,
            ]
          })}
        </FormBuilder.Row>
        <FormBuilder.Row itemsPerRow={2}>
          {FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.smallTicketCharge,
              fieldsConfig.waiveThermalPaperFees,
              fieldsConfig.zeroPlan
            ]
          })}
        </FormBuilder.Row>
        <FormBuilder.Row itemsPerRow={1}>
          {FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.merchantRemarks,
            ]
          })}
        </FormBuilder.Row>
      </FormBuilder.Section>

      <FormBuilder.Section
        title={t(`${FORM_LABEL_KEY}receipt.title`)}
        titleVariant="h3">
        <FormBuilder.Row itemsPerRow={1}>
          {FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.merchantLogo,
            ]
          })}
        </FormBuilder.Row>
      </FormBuilder.Section>

      <FormBuilder.Section
        title={t(`${FORM_LABEL_KEY}campaign.title`)}
        titleVariant="h3">
        <FormBuilder.Row itemsPerRow={2}>
          {FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.isKDollar,
              fieldsConfig.enableCampaign,
            ]
          })}
        </FormBuilder.Row>
      </FormBuilder.Section>

      <FormBuilder.Section
        title={t(`${FORM_LABEL_KEY}others.title`)}
        titleVariant="h3">
        <FormBuilder.Row itemsPerRow={2}>
          {FormBuilder.buildFields({
            formProps,
            fields: [
              fieldsConfig.cooldownTime,
              fieldsConfig.monthlyTransLimit,
            ]
          })}
        </FormBuilder.Row>
      </FormBuilder.Section>
    </FormBuilder.Section>
  );
};

export default React.memo(ConfigSection);
